import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal } from 'antd';
import './smart-protection-modal.scss';
import { useStores } from '../../../store';
import { EventType } from '@src/dtos/analytics.dto';
import { ConnectionDto } from '@src/dtos/customer.dto';
import CustomButton from '@src/components/shared/custom-button/custom-button';
import usePromiseFeedback from '@src/components/shared/promise-feedback/usePromiseFeedback';
import SmartProtectionIllustration from '@svg/smart-protection-illustration.svg?react';
import { SmartProtectionContent } from './smart-protection-content';
import { TooltipDisableReason } from '@src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import { DisableReasons } from '@src/utils/enums';

interface SmartProtectionModalProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Day 2 Smart Protection Modal.
 *
 * Displays two variants depending on the soak status of the selected connection:
 *  - **Without soak**: 7 days of traffic data already available → "Create Smart draft" is actionable.
 *  - **With soak**: Insufficient data → shows remaining days before the soak period completes.
 *
 * Triggered from the Policy Settings dropdown in the protection-header.
 */
export const SmartProtectionModal: React.FC<SmartProtectionModalProps> = observer(({ open, onClose }) => {
    const { customerStore, fwVersionStore } = useStores();
    const [isLoading, setIsLoading] = useState(false);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const selectedLocation = customerStore.selectedLocation as ConnectionDto;

    const createSmartDraftDisableReason: DisableReasons | '' = (() => {
        if (fwVersionStore.isVersionDeactivate) return DisableReasons.DEACTIVATE_VERSION;
        if (fwVersionStore.pendingVersion)      return DisableReasons.PENDING_EXIST;
        if (customerStore.isSmartProtectionRunning) return DisableReasons.SMART_DRAFT_IN_PROGRESS;
        return '';
    })();

    const isCreateSmartDraftDisabled = isLoading || !!createSmartDraftDisableReason;

    const handleCreateSmartDraft = () => {
        const serviceId = selectedLocation?.serviceId;
        if (!serviceId) return;

        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'createSmartDraft',
            location: 'addPersonalizedSmartProtectionModal',
        });

        setIsLoading(true);
        promiseFeedbackFactory({
            promise: customerStore.setSmartPolicy([serviceId]),
            successCallback: () => {
                setIsLoading(false);
                onClose();
            },
            errorCallback: () => setIsLoading(false),
            notifObj: { show: false },
        });
    };

    const handleMaybeLater = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'maybeLater',
            location: 'addPersonalizedSmartProtectionModal',
        });
        onClose();
    };

    return (
        <>
            <Modal
                open={open}
                className="smart-protection-day2-modal"
                width={680}
                closable={false}
                footer={null}
                centered
                destroyOnClose
            >
                <div className="smart-protection-modal-illustration" aria-hidden="true">
                    <SmartProtectionIllustration className="smart-protection-modal-illustration-img" />
                </div>

                <div className="smart-protection-modal-content">
                    <div className="sp-content-title">Add Personalized Smart Protection</div>

                    <SmartProtectionContent analysisDaysLeft={customerStore.selectedLocation?.networkAnalysisDaysLeft} />

                    <div className="smart-protection-modal-footer">
                        <CustomButton
                            label="Maybe later"
                            className="link-btn"
                            onClick={handleMaybeLater}
                        />
                        <TooltipDisableReason
                            disableReason={createSmartDraftDisableReason || undefined}
                            placement="top"
                        >
                            <CustomButton
                                label="Create Smart draft"
                                className={`primary-btn${isLoading ? ' loading' : ''}`}
                                onClick={handleCreateSmartDraft}
                                isDisabled={isCreateSmartDraftDisabled}
                            />
                        </TooltipDisableReason>
                    </div>
                </div>
            </Modal>
            {promiseContextHolder}
        </>
    );
});
